import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DiscoveredLead, GeneratorPhase } from '../types/sales';

interface LeadGeneratorState {
  country: string;
  state: string;
  city: string;
  distanceKm: number;
  industryKeywords: string;
  maxResults: number;

  phase: GeneratorPhase;
  discoveredLeads: DiscoveredLead[];
  isComplete: boolean;
  summary: string;
  statusMessage: string;
}

interface LeadGeneratorActions {
  setCountry: (country: string) => void;
  setState: (state: string) => void;
  setCity: (city: string) => void;
  setDistanceKm: (km: number) => void;
  setIndustryKeywords: (keywords: string) => void;
  setMaxResults: (max: number) => void;

  setPhase: (phase: GeneratorPhase) => void;
  setIsComplete: (v: boolean) => void;
  setSummary: (s: string) => void;
  setStatusMessage: (m: string) => void;
  setDiscoveredLeads: (leads: DiscoveredLead[]) => void;
  addDiscoveredLead: (lead: DiscoveredLead) => void;
  toggleLeadSelection: (index: number) => void;
  toggleSelectAll: () => void;
  resetSearch: () => void;
}

export const useLeadGeneratorStore = create<LeadGeneratorState & LeadGeneratorActions>()(
  persist((set, get) => ({
  country: '',
  state: '',
  city: '',
  distanceKm: 50,
  industryKeywords: '',
  maxResults: 6,

  phase: 'config',
  discoveredLeads: [],
  isComplete: false,
  summary: '',
  statusMessage: '',

  setCountry: (country) => set({ country, state: '', city: '' }),
  setState: (state) => set({ state }),
  setCity: (city) => set({ city }),
  setDistanceKm: (distanceKm) => set({ distanceKm }),
  setIndustryKeywords: (industryKeywords) => set({ industryKeywords }),
  setMaxResults: (maxResults) => set({ maxResults }),

  setPhase: (phase) => set({ phase }),
  setIsComplete: (isComplete) => set({ isComplete }),
  setSummary: (summary) => set({ summary }),
  setStatusMessage: (statusMessage) => set({ statusMessage }),
  setDiscoveredLeads: (discoveredLeads) => set({ discoveredLeads }),
  addDiscoveredLead: (lead) => set((s) => ({ discoveredLeads: [...s.discoveredLeads, lead] })),

  toggleLeadSelection: (index) =>
    set((s) => ({
      discoveredLeads: s.discoveredLeads.map((l, i) =>
        i === index ? { ...l, selected: !l.selected } : l,
      ),
    })),

  toggleSelectAll: () => {
    const allSelected = get().discoveredLeads.every((l) => l.selected);
    set((s) => ({
      discoveredLeads: s.discoveredLeads.map((l) => ({ ...l, selected: !allSelected })),
    }));
  },

  resetSearch: () =>
    set({
      phase: 'config',
      discoveredLeads: [],
      isComplete: false,
      summary: '',
      statusMessage: '',
    }),
}), {
    name: 'quazi-lead-generator',
    partialize: (state) => ({
      country: state.country,
      state: state.state,
      city: state.city,
      distanceKm: state.distanceKm,
      industryKeywords: state.industryKeywords,
      maxResults: state.maxResults,
      phase: state.phase === 'running' ? ('config' as const) : state.phase,
      discoveredLeads: state.discoveredLeads,
      isComplete: state.isComplete,
      summary: state.summary,
    }),
  })
);
